import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Check, Lock, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { copy } from '@/copy';

const DashboardPage = () => {
  const { user, axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [checkpoints, setCheckpoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const api = axiosAuth();
        const [progressRes, checkpointsRes] = await Promise.all([
          api.get('/progress'),
          api.get('/checkpoints')
        ]);
        setProgress(progressRes.data);
        setCheckpoints(checkpointsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(copy.dashboard.loadError);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [axiosAuth]);

  const handleCheckpointClick = (checkpoint) => {
    if (checkpoint.is_locked) {
      toast.info(copy.dashboard.lockedToast);
      return;
    }
    navigate(`/checkpoint/${checkpoint.id}`);
  };

  const [showMapIntro, setShowMapIntro] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('map_intro_seen') && !progress?.is_complete) {
      setShowMapIntro(true);
    } else {
      setShowMapIntro(false);
    }
  }, [progress]);

  const handleDismissIntro = () => {
    localStorage.setItem('map_intro_seen', 'true');
    setShowMapIntro(false);
  };

  const progressPercentage = progress
    ? (progress.completed_checkpoints.length / progress.total_checkpoints) * 100
    : 0;

  const leftLoop = checkpoints.filter(c => c.order <= 3);
  const rightLoop = checkpoints.filter(c => c.order > 3);
  const leftComplete = leftLoop.length === 3 && leftLoop.every(c => c.is_completed);
  const rightStarted = rightLoop.some(c => c.is_completed);

  // === Infinity Map Layout Constants ===
  const INFINITY_PATH = "M 550,300 C 415,80 170,80 170,300 C 170,520 415,520 550,300 C 685,80 930,80 930,300 C 930,520 685,520 550,300 Z";

  const NODE_POS = {
    'foundation':          { cx: 375, cy: 145 },
    'faith-emotions':      { cx: 185, cy: 305 },
    'intimacy-commitment': { cx: 375, cy: 455 },
    'stability':           { cx: 725, cy: 455 },
    'family-future':       { cx: 915, cy: 305 },
    'destination':         { cx: 725, cy: 145 },
  };

  // These labels mirror /app/backend/server.py CHECKPOINTS serious_questions
  // in abbreviated 2-line form for the SVG node captions. If you change the
  // backend CHECKPOINTS copy, also update these to stay in sync.
  const MAP_QUESTIONS = {
    'foundation': ['What do you truly value?'],
    'faith-emotions': ['How do your beliefs shape', 'the way you feel safe?'],
    'intimacy-commitment': ['What does commitment', 'mean to you personally?'],
    'stability': ['What does emotional', 'stability feel like to you?'],
    'family-future': ['How do you relate to', 'building a family or future?'],
    'destination': ['Where are you going', 'in your own life?'],
  };

  const LEFT_IDS = ['foundation', 'faith-emotions', 'intimacy-commitment'];

  const getNameLines = (name) => {
    if (name.length > 18 && name.includes(' & ')) {
      const idx = name.indexOf(' & ');
      return [name.slice(0, idx + 2), name.slice(idx + 3)];
    }
    return [name];
  };

  // Next unlocked checkpoint (for pulsing indicator)
  const nextUnlocked = checkpoints.find(cp => !cp.is_completed && !cp.is_locked);
  const nextPos = nextUnlocked ? NODE_POS[nextUnlocked.id] : null;
  const allComplete = checkpoints.length > 0 && checkpoints.every(cp => cp.is_completed);

  // Shared checkpoint card renderer (mobile)
  const CheckpointCard = (checkpoint) => {
    const hintLabel = copy.dashboard.checkpointHints[checkpoint.id];
    const showBeginHere = checkpoint.order === 1 && !checkpoint.is_completed && !checkpoint.is_locked;
    return (
      <Card
        key={checkpoint.id}
        className={`border-none shadow-md rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg ${
          checkpoint.is_locked ? 'opacity-60' : ''
        }`}
        onClick={() => handleCheckpointClick(checkpoint)}
        data-testid={`checkpoint-card-${checkpoint.id}`}
      >
        <div className={`h-1 ${
          checkpoint.is_completed ? 'bg-[#5E7F63]' :
          checkpoint.is_locked ? 'bg-gray-300' : 'bg-[#B89B5E]'
        }`}></div>
        <CardContent className="p-5">
          {showBeginHere && (
            <p className="text-xs text-[#B89B5E] italic mb-2">{copy.dashboard.beginHere}</p>
          )}
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-full bg-[#F5F5F5] flex items-center justify-center text-sm font-medium text-[#1D2D44]">
              {checkpoint.order}
            </span>
            {checkpoint.is_completed && (
              <span className="w-5 h-5 rounded-full bg-[#5E7F63] flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </span>
            )}
            {checkpoint.is_locked && <Lock className="w-4 h-4 text-gray-400" />}
          </div>
          <h4 className="font-serif text-lg text-[#1D2D44] mb-1">{checkpoint.name}</h4>
          <p className="text-sm text-[#3A4A5A]">{checkpoint.subtitle}</p>
          {hintLabel && (
            <p className="text-xs text-[#B89B5E] italic mt-2">{hintLabel}</p>
          )}
          {!checkpoint.is_locked && !checkpoint.is_completed && (
            <div className="mt-3">
              <span className="text-[#B89B5E] text-sm font-medium flex items-center">
                {copy.common.continue}<ChevronRight className="w-4 h-4 ml-1" />
              </span>
            </div>
          )}
          {checkpoint.is_completed && (
            <div className="mt-3">
              <span className="text-[#5E7F63] text-sm font-medium flex items-center">
                {copy.common.review}<ChevronRight className="w-4 h-4 ml-1" />
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#1D2D44] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#1D2D44] font-medium">{copy.dashboard.loadingJourney}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">

      {/* How to Use This Map — intro overlay (shown once per user) */}
      {showMapIntro && (
        <div
          className="fixed inset-0 bg-[#F5F5F5] z-50 overflow-y-auto flex items-center justify-center"
          data-testid="map-intro-overlay"
        >
          <div className="w-full max-w-2xl mx-auto px-8 py-16">

            {/* Brand label */}
            <p className="text-xs uppercase tracking-[0.3em] text-[#B89B5E] text-center mb-10">
              {copy.mapIntro.brandLabel}
            </p>

            {/* Divider */}
            <div className="border-t border-[#B89B5E]/40 mb-10"></div>

            {/* Title */}
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1D2D44] text-center mb-5">
              {copy.mapIntro.title}
            </h1>

            {/* Subtitle */}
            <p className="font-serif text-[#B89B5E] italic text-center text-lg sm:text-xl mb-7">
              {copy.mapIntro.subtitle}
            </p>

            {/* Divider */}
            <div className="border-t border-[#B89B5E]/40 mb-10"></div>

            {/* Body */}
            <div className="space-y-6 text-center max-w-xl mx-auto mb-12">
              <p className="text-[#1D2D44] leading-relaxed text-base sm:text-lg">{copy.mapIntro.line1}</p>
              <p className="text-[#1D2D44] leading-relaxed text-base sm:text-lg">{copy.mapIntro.line2}</p>
              <p className="text-[#1D2D44] leading-relaxed text-base sm:text-lg">{copy.mapIntro.line3}</p>
              <p className="text-[#1D2D44] leading-relaxed text-base sm:text-lg">{copy.mapIntro.line4}</p>
              <p className="text-[#1D2D44] leading-relaxed text-base sm:text-lg">{copy.mapIntro.line5}</p>
            </div>

            {/* Divider */}
            <div className="border-t border-[#B89B5E]/40 mb-10"></div>

            {/* CTA */}
            <div className="flex justify-center">
              <Button
                onClick={handleDismissIntro}
                className="bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full px-10 py-6 text-base font-medium transition-all duration-300 hover:scale-[1.02]"
                data-testid="map-intro-begin-btn"
              >
                {copy.mapIntro.cta}
              </Button>
            </div>

          </div>
        </div>
      )}

      <Navigation />

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-6 animate-fade-in">
          <h1 className="font-serif text-3xl sm:text-4xl text-[#1D2D44] mb-2">
            {copy.dashboard.welcomeBack(user?.name?.split(' ')[0])}
          </h1>
          <p className="text-[#3A4A5A]">
            {copy.dashboard.welcomeDescription}
          </p>
        </div>

        {/* Orientation Note */}
        <p className="text-[#424E59] text-sm mb-8 max-w-2xl">
          {copy.dashboard.orientationNote}
        </p>

        {/* Journey Progress Card */}
        <Card className="mb-8 border-none shadow-lg rounded-2xl overflow-hidden animate-slide-up">
          <div className="bg-[#1D2D44] p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[#B89B5E] text-sm uppercase tracking-wider mb-1">{copy.dashboard.journeyLabel}</p>
                <h2 className="font-serif text-2xl text-white">{copy.dashboard.journeyTitle}</h2>
                <p className="text-white/50 text-xs mt-1 italic tracking-wide">{copy.dashboard.journeySubtitle}</p>
              </div>
              <div className="w-14 h-14 bg-[#B89B5E]/20 rounded-full flex items-center justify-center">
                <MapPin className="w-7 h-7 text-[#B89B5E]" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-white/70">{copy.dashboard.progressLabel}</span>
                <span className="text-[#B89B5E]">
                  {copy.dashboard.progressCount(progress?.completed_checkpoints.length || 0, progress?.total_checkpoints || 6)}
                </span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#5E7F63] transition-all duration-1000 ease-out rounded-full"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
          </div>
          
          {progress?.is_complete && (
            <CardContent className="p-6 bg-[#A3B8A6]/20">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#5E7F63] rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1D2D44]">{copy.dashboard.journeyComplete}</p>
                    <p className="text-sm text-[#424E59]">{copy.dashboard.journeyCompleteDescription}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to="/summary">
                    <Button
                      data-testid="view-summary-btn"
                      variant="outline"
                      className="border-[#1D2D44] text-[#1D2D44] hover:bg-[#1D2D44]/5 rounded-full"
                    >
                      {copy.dashboard.viewSummary}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                  <Link to="/profile">
                    <Button
                      data-testid="build-profile-btn"
                      className="bg-[#1D2D44] text-[#C9A84C] hover:bg-[#1F1F1F] rounded-full"
                    >
                      Build Your Profile
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Relationship Map — Infinity Layout */}
        <div className="mb-8">
          {/* Header — matching reference design */}
          <div className="text-center mb-8">
            <div className="border-t border-[#B89B5E]/40 max-w-md mx-auto mb-4"></div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#B89B5E] mb-3">{copy.mapIntro.brandLabel}</p>
            <div className="border-t border-[#B89B5E]/40 max-w-md mx-auto mb-5"></div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#1D2D44] mb-2">{copy.dashboard.journeyTitle}</h2>
            <p className="font-serif text-[#B89B5E] italic text-base">{copy.dashboard.journeySubtitle}</p>
            <div className="border-t border-[#B89B5E]/40 max-w-xs mx-auto mt-5"></div>
          </div>

          {/* SVG Infinity Map — desktop (md+) */}
          <div className="hidden md:block" data-testid="infinity-map">
            <svg viewBox="0 0 1100 560" className="w-full">

              {/* === Double-stroke figure-8 band === */}
              <path d={INFINITY_PATH} fill="none" stroke="#B8943E" strokeWidth="30" />
              <path d={INFINITY_PATH} fill="none" stroke="#D4B96A" strokeWidth="22" />
              <path d={INFINITY_PATH} fill="none" stroke="#B8943E" strokeWidth="22" strokeOpacity="0.15" />

              {/* === Animated tracing dot === */}
              <circle r="18" fill="#B89B5E" opacity="0.10">
                <animateMotion dur="16s" repeatCount="indefinite" path={INFINITY_PATH} />
              </circle>
              <circle r="10" fill="#B89B5E" opacity="0.25">
                <animateMotion dur="16s" repeatCount="indefinite" path={INFINITY_PATH} />
              </circle>
              <circle r="5" fill="#FFFFFF" opacity="0.9">
                <animateMotion dur="16s" repeatCount="indefinite" path={INFINITY_PATH} />
              </circle>

              {/* === Pulsing ripple at next unlocked checkpoint === */}
              {nextPos && (
                <g data-testid="pulse-indicator">
                  <circle cx={nextPos.cx} cy={nextPos.cy} r="24" fill="none" stroke="#B89B5E" strokeWidth="2.5" opacity="0">
                    <animate attributeName="r" values="24;40;24" dur="2.4s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.7;0;0.7" dur="2.4s" repeatCount="indefinite" />
                  </circle>
                  <circle cx={nextPos.cx} cy={nextPos.cy} r="26" fill="none" stroke="#B89B5E" strokeWidth="1.5" opacity="0">
                    <animate attributeName="r" values="28;46;28" dur="2.4s" repeatCount="indefinite" begin="0.4s" />
                    <animate attributeName="opacity" values="0.4;0;0.4" dur="2.4s" repeatCount="indefinite" begin="0.4s" />
                  </circle>
                </g>
              )}

              {/* === All-complete celebration pulses on every node === */}
              {allComplete && checkpoints.map((cp) => {
                const pos = NODE_POS[cp.id];
                if (!pos) return null;
                const delay = `${cp.order * 0.3}s`;
                return (
                  <g key={`pulse-${cp.id}`}>
                    <circle cx={pos.cx} cy={pos.cy} r="24" fill="none" stroke="#5E7F63" strokeWidth="3" opacity="0">
                      <animate attributeName="r" values="24;44;24" dur="3s" repeatCount="indefinite" begin={delay} />
                      <animate attributeName="opacity" values="0.8;0;0.8" dur="3s" repeatCount="indefinite" begin={delay} />
                    </circle>
                    <circle cx={pos.cx} cy={pos.cy} r="28" fill="none" stroke="#B89B5E" strokeWidth="1.5" opacity="0">
                      <animate attributeName="r" values="28;50;28" dur="3s" repeatCount="indefinite" begin={`${cp.order * 0.3 + 0.5}s`} />
                      <animate attributeName="opacity" values="0.5;0;0.5" dur="3s" repeatCount="indefinite" begin={`${cp.order * 0.3 + 0.5}s`} />
                    </circle>
                  </g>
                );
              })}

              {/* === "ALIGNMENT CHECKPOINT" label above center === */}
              <text x="550" y="258" textAnchor="middle" fill="#1D2D44" fontSize="11" fontWeight="600" letterSpacing="2.5">ALIGNMENT CHECKPOINT</text>

              {/* === Loop section labels === */}
              <text x="420" y="308" textAnchor="middle" fill="#B89B5E" fontSize="13" fontStyle="italic" fontFamily="Georgia, serif">Know Yourself</text>
              <text x="680" y="308" textAnchor="middle" fill="#B89B5E" fontSize="13" fontStyle="italic" fontFamily="Georgia, serif">Build Together</text>

              {/* === Center alignment circle with lock icon === */}
              <g
                onClick={leftComplete && !rightStarted ? () => navigate(`/checkpoint/${rightLoop[0]?.id}`) : undefined}
                style={{ cursor: leftComplete && !rightStarted ? 'pointer' : 'default' }}
                data-testid="alignment-checkpoint-svg"
              >
                <circle cx={550} cy={300} r={35}
                  fill={leftComplete ? (rightStarted ? '#5E7F63' : '#1D2D44') : '#1D2D44'}
                  stroke="#B89B5E" strokeWidth="2"
                />
                {leftComplete && rightStarted ? (
                  <path d="M 540,300 l7,7 l13,-13" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                ) : (
                  <g>
                    <path d={`M 544,296 V 288 C 544,281 556,281 556,288 V 296`} fill="none" stroke={leftComplete ? "#B89B5E" : "#7A8A9A"} strokeWidth="2.2" strokeLinecap="round" />
                    <rect x="540" y="296" width="20" height="15" rx="2.5" fill="none" stroke={leftComplete ? "#B89B5E" : "#7A8A9A"} strokeWidth="1.8" />
                    <circle cx={550} cy={304} r={1.8} fill={leftComplete ? "#B89B5E" : "#7A8A9A"} />
                  </g>
                )}
              </g>

              {/* === "Pause. Reflect. Decide." below center === */}
              <text x="550" y="350" textAnchor="middle" fill="#1D2D44" fontSize="12" fontStyle="italic" fontFamily="Georgia, serif">{copy.dashboard.alignmentCenter}</text>

              {/* === "Begin Here" above node 01 === */}
              <text x="375" y="112" textAnchor="middle" fill="#B89B5E" fontSize="12" fontStyle="italic" fontFamily="Georgia, serif">{copy.dashboard.beginHere}</text>

              {/* === "Your Personal Vision" above node 06 === */}
              <text x="725" y="112" textAnchor="middle" fill="#B89B5E" fontSize="12" fontStyle="italic" fontFamily="Georgia, serif">{copy.dashboard.checkpointHints.destination}</text>

              {/* === Hint tags with bordered boxes === */}
              {/* Pause & Reflect — between nodes 01 and 02 */}
              <g>
                <rect x="232" y="217" width="114" height="24" rx="4" fill="#F5F5F5" stroke="#B89B5E" strokeWidth="0.8" />
                <text x="289" y="233" textAnchor="middle" fill="#1D2D44" fontSize="10.5" fontWeight="500">{copy.dashboard.checkpointHints.foundation}</text>
                <line x1="346" y1="229" x2="362" y2="210" stroke="#B89B5E" strokeWidth="0.6" />
              </g>
              {/* Be Honest Here — between nodes 02 and 03 */}
              <g>
                <rect x="237" y="382" width="104" height="24" rx="4" fill="#F5F5F5" stroke="#B89B5E" strokeWidth="0.8" />
                <text x="289" y="398" textAnchor="middle" fill="#1D2D44" fontSize="10.5" fontWeight="500">{copy.dashboard.checkpointHints['faith-emotions']}</text>
                <line x1="341" y1="394" x2="360" y2="415" stroke="#B89B5E" strokeWidth="0.6" />
              </g>
              {/* This Requires Clarity — between nodes 04 and 05 */}
              <g>
                <rect x="748" y="382" width="146" height="24" rx="4" fill="#F5F5F5" stroke="#B89B5E" strokeWidth="0.8" />
                <text x="821" y="398" textAnchor="middle" fill="#1D2D44" fontSize="10.5" fontWeight="500">{copy.dashboard.checkpointHints.stability}</text>
                <line x1="748" y1="394" x2="732" y2="405" stroke="#B89B5E" strokeWidth="0.6" />
              </g>

              {/* === Checkpoint nodes and text labels === */}
              {checkpoints.map((cp) => {
                const pos = NODE_POS[cp.id];
                if (!pos) return null;

                const isLeft = LEFT_IDS.includes(cp.id);
                const textX = isLeft ? 105 : 995;
                const fill = cp.is_completed ? '#5E7F63' : cp.is_locked ? '#D5CDC5' : '#1D2D44';
                const numColor = cp.is_completed ? 'white' : cp.is_locked ? '#B5ADA7' : 'white';
                const num = String(cp.order).padStart(2, '0');

                const nameLines = getNameLines(cp.name);
                const qLines = MAP_QUESTIONS[cp.id] || [];
                const nameH = 18;
                const qH = 15;
                const gap = 8;
                const totalH = nameLines.length * nameH + gap + qLines.length * qH;
                const baseY = pos.cy - totalH / 2 + 12;

                return (
                  <g key={cp.id}>
                    {/* Clickable node circle */}
                    <g onClick={() => handleCheckpointClick(cp)} style={{ cursor: cp.is_locked ? 'default' : 'pointer' }}>
                      <circle cx={pos.cx} cy={pos.cy} r={22} fill={fill} />
                      {cp.is_completed ? (
                        <path d={`M ${pos.cx - 8},${pos.cy} l6,6 l10,-10`} fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      ) : (
                        <text x={pos.cx} y={pos.cy + 5} textAnchor="middle" fill={numColor} fontSize="13" fontWeight="700">{num}</text>
                      )}
                    </g>

                    {/* Text label block (outside the loop) */}
                    <g onClick={() => handleCheckpointClick(cp)} style={{ cursor: cp.is_locked ? 'default' : 'pointer' }}>
                      {nameLines.map((line, i) => (
                        <text key={`n-${i}`} x={textX} y={baseY + i * nameH}
                          textAnchor="middle" fill="#1D2D44" fontSize="15" fontWeight="700" fontFamily="Georgia, serif">
                          {line}
                        </text>
                      ))}
                      {qLines.map((line, i) => (
                        <text key={`q-${i}`} x={textX} y={baseY + nameLines.length * nameH + gap + i * qH}
                          textAnchor="middle" fill="#1D2D44" fontSize="12" fontStyle="italic" fontFamily="Georgia, serif">
                          {line}
                        </text>
                      ))}
                    </g>
                  </g>
                );
              })}

            </svg>
          </div>

          {/* Card layout — mobile only (md:hidden) */}
          <div className="md:hidden flex flex-col gap-4 stagger-children">

            {/* Left Loop */}
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-widest text-[#5E7F63] font-medium pl-1 mb-2">
                {copy.dashboard.leftLoop}
              </p>
              {leftLoop.map(CheckpointCard)}
            </div>

            {/* Center — Alignment Checkpoint */}
            <div className="w-full flex flex-col">
              <p className="text-xs uppercase tracking-widest text-[#B89B5E] font-medium mb-2 text-center">
                {copy.dashboard.alignmentLabel}
              </p>
              <div className={`rounded-2xl border-2 p-5 flex flex-col items-center justify-center text-center min-h-[160px] transition-all duration-500 ${
                leftComplete
                  ? rightStarted
                    ? 'border-[#5E7F63] bg-[#5E7F63]/5'
                    : 'border-[#B89B5E] bg-[#1D2D44]/5 shadow-md'
                  : 'border-dashed border-gray-300 opacity-50'
              }`} data-testid="alignment-checkpoint-card">
                {leftComplete ? (
                  rightStarted ? (
                    <>
                      <span className="w-8 h-8 rounded-full bg-[#5E7F63] flex items-center justify-center mb-3">
                        <Check className="w-4 h-4 text-white" />
                      </span>
                      <p className="font-serif text-[#1D2D44] text-sm italic mb-2">"{copy.dashboard.alignmentCenter}"</p>
                      <p className="text-xs text-[#5E7F63] uppercase tracking-wider font-medium">{copy.dashboard.alignmentAcknowledged}</p>
                    </>
                  ) : (
                    <>
                      <p className="font-serif text-[#1D2D44] text-base leading-snug italic mb-3">"{copy.dashboard.alignmentCenter}"</p>
                      <div className="border-t border-[#B89B5E]/30 w-full mb-3"></div>
                      <p className="text-xs text-[#B89B5E] uppercase tracking-wider font-medium mb-3">{copy.dashboard.alignmentPause}</p>
                      <p className="font-serif text-[#1D2D44] text-sm leading-relaxed mb-4 italic">"{copy.dashboard.alignmentPrompt}"</p>
                      <Button
                        onClick={() => navigate(`/checkpoint/${rightLoop[0]?.id}`)}
                        className="bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full text-sm px-4 py-2 h-auto"
                        data-testid="alignment-continue-btn"
                      >
                        {copy.dashboard.alignmentCta}<ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </>
                  )
                ) : (
                  <>
                    <Lock className="w-5 h-5 text-gray-400 mb-2" />
                    <p className="font-serif text-[#1D2D44]/40 text-sm italic mb-2">"{copy.dashboard.alignmentCenter}"</p>
                    <p className="text-xs text-[#424E59]">{copy.dashboard.alignmentLocked}</p>
                  </>
                )}
              </div>
            </div>

            {/* Right Loop */}
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-widest text-[#5E7F63] font-medium pl-1 mb-2">
                {copy.dashboard.rightLoop}
              </p>
              {rightLoop.map(CheckpointCard)}
            </div>

          </div>
        </div>

        {/* Footer with gold divider */}
        <div className="border-t border-[#B89B5E]/40 max-w-md mx-auto mb-4"></div>
        <p className="text-center text-[#424E59] text-sm">
          {copy.dashboard.mapPrivacyNote}
        </p>

        {progress?.is_complete && (
          <Card className="max-w-lg mx-auto mt-10 border-none shadow-lg rounded-2xl overflow-hidden" data-testid="completion-message-card">
            <div className="h-1 bg-[#B89B5E]"></div>
            <CardContent className="p-8 sm:p-10">
              <div className="space-y-4 text-[#3A4A5A] text-sm leading-relaxed text-center">
                <p className="font-serif text-lg text-[#1D2D44]">{copy.dashboard.completionLine1}</p>
                <p>{copy.dashboard.completionLine2}</p>
                <p className="font-serif text-lg text-[#1D2D44]">{copy.dashboard.completionLine3}</p>
                <p>{copy.dashboard.completionLine4}</p>
                <p>{copy.dashboard.completionLine5}</p>
                <p className="text-[#B89B5E] font-medium italic">{copy.dashboard.completionLine6}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default DashboardPage;
